import type { InventoryDamageNeed } from '../janus-engine/inventory/ability-damage.js';

/** Ability and zone-entry lanes hydrate exact-id gaps from preflight and final status recording alike. */
export function missingDefinitionId(reason: string): string | undefined {
  return /^(?:preflight:|zone-entry:|commit:)(?:volley:recompile:def-missing|.*(?:definition|prop-template)-not-held):([0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12})$/i.exec(reason)?.[1]?.toUpperCase();
}

/** Keep internal compile diagnostics in telemetry while the caster gets an actionable explanation. */
export function volleyResolutionWarning(reason: string, abilityName: string, targetName: string): string | undefined {
  if (!reason.includes('volley:recompile')) return undefined;
  const cause = missingDefinitionId(reason) ? "an effect definition couldn't be loaded" : "the target's effects couldn't be updated";
  return `${abilityName} couldn't resolve against ${targetName}: ${cause}. Please try again.`;
}

/** The resolver batches every dry-run/stray candidate's facts. Older single-need results retain their pre-draw parser. */
export function inventoryDamageNeeds(reason: string, needs?: readonly InventoryDamageNeed[]): InventoryDamageNeed[] {
  if (!reason.startsWith('preflight:')) return [];
  if (needs?.length) return [...new Map(needs.map(need => [`${need.kind}:${need.id.toUpperCase()}`,
    { ...need, id: need.id.toUpperCase() }])).values()];
  const match = /^preflight:(?:stray:)?inventory-damage:(corpse|links|vitality)-not-held:([0-9A-F-]{36})$/i.exec(reason);
  return match ? [{ kind: match[1]!.toLowerCase() as InventoryDamageNeed['kind'], id: match[2]!.toUpperCase() }] : [];
}

/** One hydration round, including all chunks when the read endpoint's 256-id request limit is exceeded. No facts means no fetch. */
export async function hydrateInventoryDamageNeeds(needs: readonly InventoryDamageNeed[],
  links: (ids: string[]) => Promise<boolean>, corpse: (id: string) => Promise<string | null>,
  vitality: (id: string) => Promise<boolean> = async () => false): Promise<boolean> {
  const linkIds = [...new Set(needs.filter(need => need.kind === 'links').map(need => need.id))];
  const corpseIds = [...new Set(needs.filter(need => need.kind === 'corpse').map(need => need.id))];
  const vitalityIds = [...new Set(needs.filter(need => need.kind === 'vitality').map(need => need.id))];
  const reads: Promise<boolean>[] = [...corpseIds.map(async id => await corpse(id) === null), ...vitalityIds.map(vitality)];
  for (let i = 0; i < linkIds.length; i += 256) reads.push(links(linkIds.slice(i, i + 256)));
  return reads.length > 0 && (await Promise.all(reads)).every(Boolean);
}
